import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumb, Container, Segment } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { getBreadcrumbs } from '@plone/volto/actions/breadcrumbs/breadcrumbs';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';

const messages = defineMessages({
  home: {
    id: 'Home',
    defaultMessage: 'Home',
  },
  breadcrumbs: {
    id: 'Breadcrumbs',
    defaultMessage: 'Breadcrumbs',
  },
  controlpanel: {
    id: 'Site Setup',
    defaultMessage: 'Site Setup',
  },
  search: {
    id: 'Search',
    defaultMessage: 'Search',
  },
  sitemap: {
    id: 'Sitemap',
    defaultMessage: 'Sitemap',
  },
});

const BreadcrumbsComponent = ({ pathname }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { pathname: realPath } = useLocation();

  const specialPaths = useMemo(() => [
      {
        match: '/controlpanel',
        items: [{ url: '/controlpanel', title: intl.formatMessage(messages.controlpanel) }],
      },
      {
        match: '/search',
        items: [{ url: '/search', title: intl.formatMessage(messages.search) }],
      },
      {
        match: '/sitemap',
        items: [{ url: '/sitemap', title: intl.formatMessage(messages.sitemap) }],
      },
  ], [intl]);

  const items = useSelector(
    (state) => {
      const special = specialPaths.find((p) => realPath.startsWith(p.match));
      return special ? special.items : state.breadcrumbs.items;
    },
    shallowEqual,
  );

  const root = useSelector((state) => state.breadcrumbs.root);

  useEffect(() => {
    if (!hasApiExpander('breadcrumbs', getBaseUrl(pathname))) {
      dispatch(getBreadcrumbs(getBaseUrl(pathname)));
    }
  }, [dispatch, pathname]);

    useEffect(() => {
      if (!hasApiExpander('header_info', getBaseUrl(pathname))) {
        dispatch(getHeaderInfo(getBaseUrl(pathname)));
      }
  }, [dispatch, pathname]);

  return (
    <nav
      aria-label={intl.formatMessage(messages.breadcrumbs)}
      className="breadcrumbs d-print-none"
    >
      <Breadcrumb as="ul" className='list-unstyled'>
        <li>
          <Link
            to={root || '/'}
            className="section">
            {intl.formatMessage(messages.home)}
          </Link>
        </li>
        {items.map((item, index, items) => [
          <li key={`divider-${item.url}`} >
            <Breadcrumb.Divider />
            {index < items.length - 1 ? (
              <Link key={item.url} to={item.url} className="section">
                {item.title}
              </Link>
            ) : (
              <Breadcrumb.Section as="strong" key={item.url} active>
                {item.title}
              </Breadcrumb.Section>
            )}
          </li>
        ])}
      </Breadcrumb>
    </nav>
  );
};

BreadcrumbsComponent.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default BreadcrumbsComponent;
